import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AccessTime, DoneAll, Done, ErrorOutline, Instagram, Send } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import AppChip from "../../common/app-chip";
import {
  INSTAGRAM_CONVERSATIONS,
  INSTAGRAM_CONVERSATION_DETAIL,
  INSTAGRAM_CONVERSATION_SEND,
} from "../../config/urls";
import { errorMessage, useInstagramSocket } from "./shared";
import InstagramBanners from "./InstagramBanners";

// Phase 1 live chat (spec 9 / 10.6): Instagram conversations, window timer, bot pause, agent replies.

const SOURCE_LABEL = {
  DM: "Direct message",
  COMMENT: "Started from a comment",
  STORY_MENTION: "Started from a story mention",
  STORY_REPLY: "Started from a story reply",
  AD: "From an ad",
  ICEBREAKER: "From an ice breaker",
};
const PAUSE_REASON = {
  instagram_app: "Replied from Instagram app",
  agent_reply: "An agent replied",
  manual: "Paused by an agent",
  handover_keyword: "User asked for an agent",
  no_match: "Bot couldn't answer twice",
  rule: "Handed over by an automation",
};

const timeLeft = (until) => {
  const ms = new Date(until) - Date.now();
  if (ms <= 0) return "0m";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m`;
};

const WindowChip = ({ conversation }) => {
  const { mode } = conversation;
  if (mode === "open") return <AppChip color="success" variant="outlined" label={`${timeLeft(conversation.window_expires_at)} left`} />;
  if (mode === "human_agent")
    return <AppChip color="warning" variant="outlined" label={`Human agent reply · ${timeLeft(conversation.human_agent_until)} left`} />;
  if (mode === "readonly") return <AppChip label="Account disconnected" />;
  return <AppChip label="Window closed" />;
};

const StatusIcon = ({ message }) => {
  if (message.status === "queued") return <AccessTime sx={{ fontSize: 14 }} />;
  if (message.status === "seen") return <DoneAll sx={{ fontSize: 14, color: "#1e88e5" }} />;
  if (message.status === "failed")
    return (
      <Tooltip title={message.error || "Failed"}>
        <ErrorOutline sx={{ fontSize: 14, color: "#d32f2f" }} />
      </Tooltip>
    );
  return <Done sx={{ fontSize: 14 }} />;
};

const Attachment = ({ att }) => {
  if (!att.url) return null;
  if (["image", "story_mention", "story", "animated_image"].includes(att.type))
    return <Box component="img" src={att.url} alt="" sx={{ maxWidth: 220, maxHeight: 320, borderRadius: 1.5, display: "block", mb: 0.5 }} />;
  if (att.type === "video") return <Box component="video" src={att.url} controls sx={{ maxWidth: 260, borderRadius: 1.5, mb: 0.5 }} />;
  if (att.type === "audio") return <Box component="audio" src={att.url} controls sx={{ maxWidth: 260, mb: 0.5 }} />;
  const label = { share: "Shared post", ig_reel: "Shared reel", reel: "Shared reel" }[att.type] || "Attachment";
  return (
    <Link href={att.url} target="_blank" rel="noopener" variant="body2" display="block" mb={0.5}>
      {label} ↗
    </Link>
  );
};

const Bubble = ({ message }) => {
  const outbound = message.direction === "out";
  let body;
  if (message.is_deleted) body = <Typography variant="body2" fontStyle="italic" color="text.secondary">Message unsent</Typography>;
  else if (message.msg_type === "unsupported")
    body = <Typography variant="body2" fontStyle="italic">Message type not supported, open Instagram</Typography>;
  else
    body = (
      <>
        {message.msg_type === "story_mention" && <Typography variant="caption" display="block" mb={0.5}>Mentioned you in their story</Typography>}
        {message.msg_type === "story_reply" && <Typography variant="caption" display="block" mb={0.5}>Replied to your story</Typography>}
        {message.attachments.map((att, i) => <Attachment key={i} att={att} />)}
        {message.msg_type === "postback" && <Typography variant="caption" display="block">Tapped a button</Typography>}
        {message.text && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{message.text}</Typography>}
      </>
    );

  const meta = [];
  if (message.sent_by === "instagram_app") meta.push("Replied from Instagram app");
  if (message.sent_by === "agent") meta.push(message.agent_name || "Agent");
  if (message.sent_by === "bot") meta.push(`Bot${message.rule ? ` · ${message.rule}` : ""}`);
  if (message.tag === "HUMAN_AGENT") meta.push("Human agent reply");

  return (
    <Box display="flex" justifyContent={outbound ? "flex-end" : "flex-start"} mb={1.25}>
      <Box maxWidth="72%">
        <Box sx={{
          px: 1.5, py: 1, borderRadius: 2.5,
          bgcolor: outbound ? (message.status === "failed" ? "#fdecea" : "#e3f0ff") : "#f1f3f4",
        }}>
          {body}
        </Box>
        {message.reactions.length > 0 && (
          <Typography variant="caption" sx={{ ml: 1 }}>{message.reactions.map((r) => r.emoji).join(" ")}</Typography>
        )}
        <Box display="flex" alignItems="center" gap={0.5} justifyContent={outbound ? "flex-end" : "flex-start"} mt={0.25}>
          <Typography variant="caption" color="text.secondary">
            {[...meta, new Date(message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })].join(" · ")}
          </Typography>
          {outbound && <StatusIcon message={message} />}
        </Box>
        {message.status === "failed" && (
          <Typography variant="caption" color="error" display="block" textAlign="right">
            {message.error_code === "WINDOW_CLOSED" ? "Outside the messaging window" : message.error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// listHeader: optional content above the conversation list (the Conversation page's Chats header + tabs).
const InstagramLiveChat = ({ accounts, request, notify, sx, listHeader }) => {
  const [filters, setFilters] = useState({ account: "", q: "", assigned: "" });
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null); // { conversation, messages }
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadList = useCallback(async () => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    try {
      setConversations((await request("get", `${INSTAGRAM_CONVERSATIONS}?${params}`)).conversations);
    } catch (error) {
      notify(errorMessage(error, "Could not load conversations."), "error");
    }
  }, [filters, request, notify]);

  const loadThread = useCallback(async () => {
    if (!selectedId) return;
    try {
      setThread(await request("get", INSTAGRAM_CONVERSATION_DETAIL(selectedId)));
    } catch (error) {
      notify(errorMessage(error, "Could not load the conversation."), "error");
    }
  }, [selectedId, request, notify]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setThread(null);
    loadThread();
  }, [loadThread]);

  // Pushed by the server when a chat changes (new message, delivery/seen, bot paused, assigned...).
  useInstagramSocket(({ conversations: changed, reconnected }) => {
    if (!reconnected && !changed.size) return;
    loadList();
    if (reconnected || changed.has(null) || changed.has(selectedId)) loadThread();
  });

  const lastMessageId = thread?.messages[thread.messages.length - 1]?.id;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [lastMessageId]);

  const patch = async (data) => {
    try {
      const res = await request("patch", INSTAGRAM_CONVERSATION_DETAIL(selectedId), data);
      setThread((t) => ({ ...t, conversation: res.conversation }));
      loadList();
    } catch (error) {
      notify(errorMessage(error, "Could not update the conversation."), "error");
    }
  };

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const res = await request("post", INSTAGRAM_CONVERSATION_SEND(selectedId), { text: draft });
      setThread((t) => ({ conversation: res.conversation, messages: [...t.messages, ...res.messages] }));
      setDraft("");
      setTimeout(loadThread, 1500);
    } catch (error) {
      notify(errorMessage(error, "Could not send the message."), "error");
    } finally {
      setSending(false);
    }
  };

  const conversation = thread?.conversation;
  const contact = conversation?.contact;
  const canSend = conversation && ["open", "human_agent"].includes(conversation.mode);

  return (
    <Box display="flex" border="1px solid #eceff1" borderRadius="12px" bgcolor="#fff" overflow="hidden"
      sx={{ height: { xs: "auto", md: "calc(100vh - 260px)" }, minHeight: 480, flexDirection: { xs: "column", md: "row" }, ...sx }}>
      {/* Conversation list */}
      <Box sx={{ width: { xs: "100%", md: 300, lg: 320 }, flexShrink: 0, borderRight: { md: "1px solid #eceff1" }, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {listHeader}
        <Box p={1.5} display="flex" flexDirection="column" gap={1} borderBottom="1px solid #eceff1">
          <TextField size="small" placeholder="Search name or username" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          <Box display="flex" gap={1}>
            <TextField select size="small" label="Account" value={filters.account} sx={{ flex: 1 }}
              onChange={(e) => setFilters({ ...filters, account: e.target.value })}>
              <MenuItem value="">All accounts</MenuItem>
              {accounts.map((a) => <MenuItem key={a.id} value={a.id}>@{a.username}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Show" value={filters.assigned} sx={{ width: 120 }}
              onChange={(e) => setFilters({ ...filters, assigned: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="me">Mine</MenuItem>
            </TextField>
          </Box>
        </Box>
        <Box flex={1} overflow="auto" sx={{ maxHeight: { xs: 320, md: "none" } }}>
          {conversations.length === 0 && (
            <Typography variant="body2" color="text.secondary" p={2}>No Instagram conversations yet.</Typography>
          )}
          {conversations.map((c) => (
            <Box key={c.id} onClick={() => setSelectedId(c.id)} display="flex" gap={1.25} p={1.5} sx={{
              cursor: "pointer", borderBottom: "1px solid #f4f5f6",
              bgcolor: c.id === selectedId ? "#f3f7ff" : "transparent", "&:hover": { bgcolor: "#f8f9fa" },
            }}>
              <Badge color="primary" badgeContent={c.unread_count} invisible={!c.unread_count}>
                <Avatar src={c.contact.profile_pic}>{(c.contact.username || "?")[0]?.toUpperCase()}</Avatar>
              </Badge>
              <Box minWidth={0} flex={1}>
                <Box display="flex" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {c.contact.username ? `@${c.contact.username}` : c.contact.name || c.contact.igsid}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" flexShrink={0}>
                    {c.last_message_at && new Date(c.last_message_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap display="block">{c.last_message_text}</Typography>
                <Box display="flex" gap={0.5} mt={0.5} alignItems="center">
                  <Instagram sx={{ fontSize: 13, color: "#c13584" }} />
                  <Typography variant="caption" color="text.secondary" noWrap>@{c.account_username}</Typography>
                  {c.bot_paused && <AppChip label="Bot paused" sx={{ height: 18, ml: 0.5 }} />}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Thread */}
      <Box flex={1} display="flex" flexDirection="column" minWidth={0} minHeight={0}>
        <InstagramBanners sx={{ m: 1.5, mb: 0 }} />
        {!conversation ? (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center" p={4}>
            <Typography variant="body2" color="text.secondary">
              {selectedId ? "Loading…" : "Select a conversation"}
            </Typography>
          </Box>
        ) : (
          <>
            <Box p={1.5} borderBottom="1px solid #eceff1" display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Avatar src={contact.profile_pic} />
              <Box flex={1} minWidth={160}>
                <Typography fontWeight={700} noWrap>
                  {contact.username ? `@${contact.username}` : contact.igsid}
                  {contact.follows_business && <AppChip label="Follows you" sx={{ ml: 1 }} />}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[contact.name, contact.follower_count != null && `${contact.follower_count} followers`, `via @${conversation.account_username}`]
                    .filter(Boolean).join(" · ")}
                </Typography>
              </Box>
              <WindowChip conversation={conversation} />
              {conversation.bot_paused ? (
                <ButtonOne variant="outlined" title="Resume bot" onClick={() => patch({ bot_paused: false })} />
              ) : (
                <ButtonOne variant="outlined" title="Pause bot" onClick={() => patch({ bot_paused: true })} />
              )}
              {conversation.assigned_agent_id ? (
                <ButtonOne variant="outlined" title={`Unassign ${conversation.assigned_agent_name}`} onClick={() => patch({ assign: null })} />
              ) : (
                <ButtonOne variant="outlined" title="Assign to me" onClick={() => patch({ assign: "me" })} />
              )}
            </Box>
            <Box px={2} py={0.75} bgcolor="#fafbfc" borderBottom="1px solid #eceff1" display="flex" alignItems="center" gap={1}>
              {conversation.source === "COMMENT" && conversation.source_ref?.thumbnail_url && (
                <Avatar variant="rounded" src={conversation.source_ref.thumbnail_url} sx={{ width: 28, height: 28 }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {conversation.source === "COMMENT" && conversation.source_ref?.comment_id ? (
                  <>
                    Started from a comment on{" "}
                    {conversation.source_ref.permalink ? (
                      <Link href={conversation.source_ref.permalink} target="_blank" rel="noopener noreferrer">your post</Link>
                    ) : "your post"}
                    : “{conversation.source_ref.text}”
                  </>
                ) : (
                  SOURCE_LABEL[conversation.source] || conversation.source
                )}
                {conversation.source === "AD" && conversation.source_ref?.ad_id ? ` (ad ${conversation.source_ref.ad_id})` : ""}
                {conversation.bot_paused && ` · Bot paused: ${PAUSE_REASON[conversation.bot_paused_reason] || conversation.bot_paused_reason}`}
              </Typography>
            </Box>

            <Box flex={1} overflow="auto" p={2} sx={{ maxHeight: { xs: 480, md: "none" } }}>
              {thread.messages.map((m) => <Bubble key={m.id} message={m} />)}
              <div ref={bottomRef} />
            </Box>

            <Box p={1.5} borderTop="1px solid #eceff1">
              {conversation.mode === "human_agent" && (
                <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                  The 24 h window has closed. Your reply goes as a Human agent reply (allowed up to 7 days after their last message).
                </Alert>
              )}
              {conversation.mode === "locked" && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  You can reply when @{contact.username || "this user"} messages again.
                </Alert>
              )}
              {conversation.mode === "readonly" && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>This account is disconnected. History is read-only.</Alert>
              )}
              <Box display="flex" gap={1} alignItems="flex-end">
                <TextField
                  fullWidth
                  multiline
                  maxRows={5}
                  size="small"
                  placeholder={canSend ? "Type a reply…" : "Replying is not available"}
                  value={draft}
                  disabled={!canSend}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  helperText={canSend && draft.length > 1000 ? `${Math.ceil(draft.length / 1000)} messages (1,000 characters each)` : " "}
                />
                <ButtonOne startIcon={<Send />} title="Send" onClick={send} disabled={!canSend || sending || !draft.trim()} />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default InstagramLiveChat;
